import { Button } from '@bambi/component-library/src/components/Button';
import { LoadingIndicator } from '@bambi/component-library/src/components/LoadingIndicator/LoadingIndicator';
import { ChatBubbleLeftEllipsisIcon } from '@heroicons/react/24/outline';
import { useCallback, useState } from 'react';
import { useNavigate } from 'react-router';

import { NotificationItem } from './NotificationItem';
import { notificationListRenderers } from './renderers/renderers';
import { useAppDispatch } from 'src/app/store/hooks';
import {
  useNotificationsNotificationsListQuery,
  useNotificationsNotificationsMarkAllReadPartialUpdateMutation,
} from 'src/common/external/bambi-api/notificationsApi';
import { EmptyState } from 'src/common/primitives/EmptyState/EmptyState';
import { show } from 'src/common/primitives/Toast/toast.slice';
import formatServerError from 'src/common/util/serverErrorFormatter';

export function NotificationList() {
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const [showMore, setShowMore] = useState(false);
  const { data, isLoading } = useNotificationsNotificationsListQuery({
    pageSize: showMore ? 100 : 10,
    isRead: false,
    isExpired: false,
  });

  const [trigger, { isLoading: isDismissing }] =
    useNotificationsNotificationsMarkAllReadPartialUpdateMutation();

  const handleMarkAllAsRead = useCallback(async () => {
    try {
      await trigger({}).unwrap();
    } catch (e) {
      dispatch(
        show({
          type: 'error',
          title: 'Failed to dismiss all notifications',
          description: formatServerError(e),
        })
      );
    }
  }, [trigger, dispatch]);

  const showLoadingState = isLoading;
  const showEmptyState = (data?.results.length ?? 0) === 0;
  const hasMore = Boolean(data?.next);

  return (
    <div className="flex min-w-[400px] max-w-[400px] flex-col gap-2">
      {showLoadingState ? (
        <div className="flex flex-row justify-center">
          <LoadingIndicator />
        </div>
      ) : (
        <>
          {!showEmptyState && (
            <div className="flex max-h-96 flex-col overflow-auto">
              {data?.results.map((notification) => {
                const renderItem =
                  notificationListRenderers[
                    notification.notification_type_name
                  ];

                if (!renderItem) {
                  console.warn(
                    'Missing component for notification_type',
                    notification.notification_type_name
                  );
                  return null;
                }

                const { Component, getMetaData } = renderItem;
                const metaData = getMetaData(notification);

                return (
                  <div key={notification.id} className="mb-2">
                    <div
                      className={`border-l-2 pl-2 ${notification.read_at ? 'border-l-transparent' : 'border-l-mint'} pb-1 hover:bg-slate-50`}
                    >
                      <NotificationItem
                        title={metaData.title}
                        notification={notification}
                        Renderer={Component}
                      />
                    </div>
                  </div>
                );
              })}
            </div>
          )}
          {hasMore && !showMore ? (
            <Button variant="ghost" onClick={() => setShowMore(true)}>
              View More
            </Button>
          ) : null}
          {showEmptyState && (
            <EmptyState
              entityName="Notifications"
              description=""
              Icon={<ChatBubbleLeftEllipsisIcon className="w-6" />}
              Title="No unread notifications found"
            />
          )}
        </>
      )}
      {!showLoadingState ? (
        <>
          <div className="border-t" />
          <div className="mt-2 flex flex-row items-center justify-between">
            <Button
              variant="anchor-inline"
              onClick={() => navigate('/notifications')}
            >
              View My Notifications
            </Button>
            {!showEmptyState ? (
              <Button
                variant="primary"
                onClick={handleMarkAllAsRead}
                loading={isDismissing}
              >
                Dismiss all
              </Button>
            ) : null}
          </div>
        </>
      ) : null}
    </div>
  );
}
